import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useQuote } from "@/context/QuoteContext";
import { GARAGE_SWATCH_HEX } from "@/lib/catalog";

export default function CollectionSection({
  id,
  eyebrow,
  chapterNumber,
  title,
  subtitle,
  intro,
  series,
  theme = "light",
  showStyles = false,
  showPalette = false,
  cardAspect = "aspect-[4/5]",
  finishPreview = false,
}) {
  const { openQuote } = useQuote();
  const navigate = useNavigate();
  const isDark = theme === "dark";
  const isExterior = id === "exterior";
  const isInterior = id === "interior";
  const isGarage = id === "garage";
  const configurableCategory = isExterior || isInterior || isGarage;
  const configureHref = isExterior
    ? "/collection/exterior"
    : isInterior
    ? "/collection/interior"
    : isGarage
    ? "/collection/garage"
    : null;

  return (
    <section
      id={id}
      className={`relative py-24 md:py-32 ${
        isDark ? "bg-brand-charcoal text-brand-bone" : "bg-brand-bone text-brand-ink"
      }`}
      data-testid={`collection-${id}`}
    >
      <div className="relative max-w-[1280px] mx-auto px-6 md:px-10">
        {/* Header row */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end mb-14 md:mb-20">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3">
              <span className={`text-[0.68rem] tracking-[0.28em] uppercase font-medium ${isDark ? "text-brand-gold" : "text-brand-gold"}`}>
                {chapterNumber} · {eyebrow}
              </span>
              <span className={`h-px w-16 ${isDark ? "bg-brand-gold/40" : "bg-brand-green/40"}`} />
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className={`mt-5 font-light leading-[1.05] tracking-[-0.02em] text-3xl md:text-4xl lg:text-5xl ${
                isDark ? "text-brand-bone" : "text-brand-green"
              }`}
              data-testid={`collection-${id}-title`}
            >
              {title}
              {subtitle && <span className="block text-brand-gold">{subtitle}</span>}
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className={`md:col-span-5 text-base md:text-lg leading-relaxed font-light ${
              isDark ? "text-brand-bone/75" : "text-brand-ink/70"
            }`}
          >
            {intro}
          </motion.p>
        </div>

        {/* Series cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {series.map((s, i) => (
            <motion.article
              key={s.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="group flex flex-col"
              data-testid={`series-card-${s.id}`}
            >
              <div className={`relative overflow-hidden ${cardAspect} ${
                s.imageFit === "contain"
                  ? isDark ? "bg-brand-charcoal" : "bg-brand-bone"
                  : ""
              }`}>
                <motion.img
                  src={s.heroImage || s.image}
                  alt={`${s.name} — ${s.tagline}`}
                  className={`w-full h-full ${s.imageFit === "contain" ? "object-contain" : "object-cover"}`}
                  initial={{ scale: 1.05 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                />
                {s.imageFit !== "contain" && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-70 group-hover:opacity-40 transition-opacity duration-500" />
                )}
              </div>

              <div className="mt-5 flex flex-col flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className={`text-xl md:text-2xl font-normal tracking-[-0.01em] ${
                    isDark ? "text-brand-bone" : "text-brand-green"
                  }`}>
                    {s.name}
                  </h3>
                  <span className="text-[0.62rem] tracking-[0.24em] uppercase text-brand-gold font-medium">
                    {finishPreview ? "Finish" : String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <p data-testid={`series-tagline-${s.id}`} className={`mt-1 text-[0.68rem] tracking-[0.24em] uppercase font-medium ${
                  isDark ? "text-brand-gold/80" : "text-brand-gold"
                }`}>
                  {s.tagline}
                </p>
                <p data-testid={`series-description-${s.id}`} className={`mt-3 text-sm leading-relaxed font-light ${
                  isDark ? "text-brand-bone/70" : "text-brand-ink/70"
                }`}>
                  {s.description}
                </p>

                {showPalette && s.palette && (
                  <div className="mt-5" data-testid={`series-palette-${s.id}`}>
                    <p className={`text-[0.58rem] tracking-[0.28em] uppercase font-medium mb-2 ${
                      isDark ? "text-brand-gold/70" : "text-brand-gold"
                    }`}>
                      {s.palette.length} Colors Available
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {s.palette.map((p) => (
                        <span
                          key={p}
                          title={p}
                          className={`inline-block h-5 w-5 rounded-full border ${
                            isDark ? "border-brand-bone/25" : "border-brand-ink/20"
                          } shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]`}
                          style={{ backgroundColor: GARAGE_SWATCH_HEX[p] || "#8E8B87" }}
                          data-testid={`series-swatch-${s.id}-${p.replace(/\W+/g, "-").toLowerCase()}`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => {
                    if (configurableCategory) {
                      navigate(configureHref);
                    } else {
                      openQuote({ series: s.name });
                    }
                  }}
                  className={`mt-auto pt-6 self-start inline-flex items-center gap-2 text-[0.72rem] md:text-[0.76rem] tracking-[0.24em] uppercase font-semibold transition-colors duration-300 group/cta ${
                    isDark
                      ? "text-brand-gold hover:text-brand-bone"
                      : "text-brand-green hover:text-brand-gold"
                  }`}
                  data-testid={`series-configure-${s.id}`}
                >
                  {finishPreview
                    ? "Explore Collection"
                    : configurableCategory
                    ? "Configure"
                    : "Request Quote"}
                  <span aria-hidden className="text-base leading-none transition-transform duration-300 group-hover/cta:translate-x-1">→</span>
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
